import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4000/api',
  // Never hang forever: a cold backend (Render spin-up) must fail fast enough
  // for cached data and the wake-up banner to take over.
  timeout: 25000,
});

// Root of the server (baseURL minus /api) — used to ping /health while warming.
export const SERVER_ROOT = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api').replace(/\/api\/?$/, '');

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('tt_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Light offline cache ─────────────────────────────────────────────────────
// Cache successful GETs for a whitelist of read-heavy screens, then serve the
// cached copy when the device is offline or the backend is cold/unreachable.
// Transparent to the pages; only affects failed network requests.
const CACHEABLE = [
  '/posts', '/announcements', '/notifications', '/flashcards',
  '/enrollments/my-courses', '/news', '/quiz/courses', '/timetable',
  '/past-questions', '/materials', '/peer/courses',
];
const cacheKey = (config) => {
  try {
    const url = (config.baseURL || '') + config.url;
    const params = config.params ? JSON.stringify(config.params) : '';
    return 'ttc:' + url + params;
  } catch { return null; }
};
const isCacheable = (config) =>
  (config.method || 'get').toLowerCase() === 'get' &&
  CACHEABLE.some(p => (config.url || '').startsWith(p));

function writeCache(config, data) {
  const key = cacheKey(config);
  if (!key) return;
  try {
    const s = JSON.stringify({ d: data, t: Date.now() });
    if (s.length > 400_000) return;            // skip oversized payloads
    localStorage.setItem(key, s);
  } catch {
    // localStorage full — drop our cache entries and move on.
    try { Object.keys(localStorage).filter(k => k.startsWith('ttc:')).forEach(k => localStorage.removeItem(k)); } catch {}
  }
}
function readCache(config) {
  const key = cacheKey(config);
  if (!key) return null;
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

api.interceptors.response.use(
  (res) => {
    if (isCacheable(res.config)) writeCache(res.config, res.data);
    return res;
  },
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('tt_token');
      localStorage.removeItem('tt_user');
      window.location.href = '/signin';
      return Promise.reject(err);
    }
    // No response while the device is online = the backend is unreachable or
    // still warming up → let the wake-up banner take over.
    if (!err.response && typeof navigator !== 'undefined' && navigator.onLine) {
      try { window.dispatchEvent(new CustomEvent('tt-server-cold')); } catch {}
    }
    // No response = offline / network error / cold-start timeout → serve cache.
    if (!err.response && err.config && isCacheable(err.config)) {
      const cached = readCache(err.config);
      if (cached) {
        return Promise.resolve({
          data: cached.d, status: 200, statusText: 'OK (cached)',
          headers: {}, config: err.config, fromCache: true, cachedAt: cached.t,
        });
      }
    }

    // Nothing left to fall back on: a GET failed and there was no cache to
    // serve. Callers overwhelmingly swallow this (.catch(() => {})), so the
    // page just renders empty and a student can't tell a dropped request from
    // "you have no courses". Announce it once here so the UI can offer a
    // retry, instead of patching ~46 call sites.
    const isGet = (err.config?.method || 'get').toLowerCase() === 'get';
    const unrecoverable = !err.response || err.response.status >= 500;
    if (isGet && unrecoverable && !err.config?.__silent) {
      try { window.dispatchEvent(new CustomEvent('tt-load-failed')); } catch {}
    }
    return Promise.reject(err);
  }
);

export default api;

// Authenticated file download — window.open/anchor navigation can't carry
// the Bearer header, so protected export routes must be fetched as blobs.
export const downloadFile = async (path, filename) => {
  const res = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ── Platform variables (fetched from backend, cached) ─────────────────────
let _platformVarsCache = null;
export const getPlatformVariables = async () => {
  if (_platformVarsCache) return _platformVarsCache;
  try {
    const { data } = await api.get('/platform-variables');
    _platformVarsCache = data;
    return data;
  } catch {
    return { halls: FALLBACK_HALLS, faculties: Object.keys(FALLBACK_DEPARTMENTS), departments: FALLBACK_DEPARTMENTS };
  }
};

// ── Platform settings (logo, semester, pricing) ──────────────────
// TTL of 60 s so all browser sessions pick up a semester change within a minute
// even if the admin changed it in a different tab/browser.
const SETTINGS_TTL = 60_000;
let _platformSettingsCache = null;
let _platformSettingsCacheAt = 0;
export const getPlatformSettings = async () => {
  if (_platformSettingsCache && (Date.now() - _platformSettingsCacheAt) < SETTINGS_TTL) return _platformSettingsCache;
  try {
    const { data } = await api.get('/platform-settings');
    _platformSettingsCache = data.settings;
    _platformSettingsCacheAt = Date.now();
    return data.settings;
  } catch {
    const y = new Date().getFullYear();
    return { activeSemester: 'First', activeSession: `${y}/${y + 1}`, logoUrl: null, pricing: { perCoursePerMonth: 2500, perCoursePerSemester: 8000, semesterPass: 20000 } };
  }
};

// ── Course catalog — sourced from course-offerings (falls back to /courses) ────────────────────
const COURSES_TTL = 60_000;
let _coursesCache = null;
let _coursesCacheAt = {};
export const getCatalogCourses = async (semester) => {
  const key = semester || 'all';
  if (_coursesCache?.[key] && (Date.now() - (_coursesCacheAt[key] || 0)) < COURSES_TTL) return _coursesCache[key];
  try {
    const params = new URLSearchParams({ active: 'true' });
    if (semester) params.append('semester', semester);
    const { data } = await api.get(`/course-offerings?${params}`);
    // offerings shape: { offerings: [{ course: {...}, tutors: [...] }] }
    // or flat shape: { courses: [...] }
    const raw = data.offerings || data.courses || [];
    const courses = raw.map(o => o.course ? { ...o.course, tutors: o.tutors || [] } : o);
    _coursesCache = { ...(_coursesCache || {}), [key]: courses };
    _coursesCacheAt[key] = Date.now();
    return courses;
  } catch {
    // fallback to raw courses endpoint
    try {
      const params = new URLSearchParams({ active: 'true' });
      if (semester) params.append('semester', semester);
      const { data } = await api.get(`/courses?${params}`);
      const courses = data.courses || [];
      _coursesCache = { ...(_coursesCache || {}), [key]: courses };
      _coursesCacheAt[key] = Date.now();
      return courses;
    } catch { return []; }
  }
};

export const buildCourseTitleMap = (courses) => {
  const map = {};
  (courses || []).forEach(c => { map[c.code] = c.title || c.code; });
  return map;
};

export const clearPlatformCache = () => {
  _platformVarsCache     = null;
  _platformSettingsCache = null;
  _platformSettingsCacheAt = 0;
  _coursesCache          = null;
  _coursesCacheAt        = {};
};

/**
 * Given an array of course codes, return only those belonging to the
 * admin-active semester. If the catalog is empty or the call fails,
 * return the original list so UI never silently breaks.
 */
export const filterCoursesBySemester = async (courses) => {
  if (!courses || courses.length === 0) return courses;
  try {
    const settings = await getPlatformSettings();
    const activeSem = settings?.activeSemester;
    if (!activeSem) return courses; // unknown semester — show all
    // Normalise codes so "CHM 101" / "CHM101" / "chm 101" all compare equal
    const norm = s => (s || '').replace(/\s+/g, '').toUpperCase();
    const otherSem = activeSem === 'First' ? 'Second' : 'First';

    // Fetch the catalogue for BOTH semesters so we can tell which semester each
    // code positively belongs to. The golden rule below is what avoids the old
    // "0 courses" regression: we only ever HIDE a course we can prove belongs to
    // the other semester. Anything unknown (not in either catalogue) is kept.
    const [activeCat, otherCat] = await Promise.all([
      getCatalogCourses(activeSem).catch(() => []),
      getCatalogCourses(otherSem).catch(() => []),
    ]);
    const activeCodes = new Set((activeCat || []).map(c => norm(c.code)));
    const otherCodes  = new Set((otherCat  || []).map(c => norm(c.code)));

    // Supplement with the static known lists so a missing catalogue still rolls
    // the well-known 100L courses over correctly.
    (activeSem === 'First' ? COURSES_FIRST : COURSES_SECOND).forEach(c => activeCodes.add(norm(c)));
    (activeSem === 'First' ? COURSES_SECOND : COURSES_FIRST).forEach(c => { const n = norm(c); if (!activeCodes.has(n)) otherCodes.add(n); });

    const filtered = courses.filter(c => {
      const n = norm(c);
      if (activeCodes.has(n)) return true;   // belongs to active semester
      if (otherCodes.has(n))  return false;  // proven to be the other semester
      return true;                           // unknown — keep (no regression)
    });
    return filtered.length ? filtered : courses;
  } catch {
    return courses;
  }
};

// ── Fallback data (used if backend is offline) ─────────────────────
export const FALLBACK_HALLS = [
  'AOO Hostel','Awolowo Hall','BQ Hostel','CMF Hostel','Idia Hall',
  'Immanuel Hostel','Iyalode Taofeekat Hostel','Kuti Hall','Maryam Hostel',
  'Mellanby Hall','Nnamdi Azikiwe Hall','Obafemi Awolowo Hall','Off Campus',
  "Queen Elizabeth II Hall","St. Anne's Hostel",'Sultan Bello Hall',
  'Talent Hostel','Tedder Hall',
];

export const FALLBACK_DEPARTMENTS = {
  'Faculty of Science': ['Biochemistry','Botany','Chemistry','Computer Science','Geology','Mathematics','Microbiology','Physics','Statistics','Zoology'],
  'Faculty of Technology': ['Agricultural & Environmental Engineering','Chemical Engineering','Civil Engineering','Electrical & Electronics Engineering','Food Technology','Industrial & Production Engineering','Mechanical Engineering','Petroleum Engineering','Wood Products Engineering'],
  'Faculty of Agriculture & Forestry': ['Agricultural Economics','Agronomy','Animal Science','Crop Protection & Environmental Biology','Forest Resources Management','Wildlife & Ecotourism Management'],
  'College of Medicine': ['Anatomy','Biochemistry (Med)','Medicine & Surgery','Medical Laboratory Science','Nursing','Physiology','Radiography'],
  'Faculty of Pharmacy': ['Pharmacy','Pharmacology'],
  'Faculty of Veterinary Medicine': ['Veterinary Medicine'],
  'Faculty of Public Health': ['Environmental Health','Health Promotion & Education','Human Nutrition'],
};

// ── Static constants ────────────────────────
export const COURSES_FIRST  = ['CHE 101','MTH 101','MTH 102','PHY 101','PHY 103','BIO 101','COS 101'];
export const COURSES_SECOND = ['CHE 102','PHY 102','PHY 104','BIO 102','ZOO 101','ZOO 102'];
export const ALL_COURSES    = [...COURSES_FIRST, ...COURSES_SECOND];

export const LEVELS = ['PUTME', '100L', '200L', '300L', '400L', '500L', '600L'];
export const STUDENT_LEVELS = ['100L', '200L', '300L'];

export const TIMETABLE_VENUES = [
  'Abadina Primary School, University of Ibadan, Ibadan, Oyo State, Nigeria',
  'Technology Lecture Theatre, University of Ibadan, Ibadan, Oyo State, Nigeria',
  'Online (Zoom/Google Meet)',
  'Other',
];

export const validatePassword = (p) => {
  const e = [];
  if (p.length < 8)           e.push('At least 8 characters');
  if (!/[A-Z]/.test(p))       e.push('One uppercase letter');
  if (!/[a-z]/.test(p))       e.push('One lowercase letter');
  if (!/[0-9]/.test(p))       e.push('One number');
  if (!/[!@#$%^&*]/.test(p))  e.push('One symbol (!@#$%^&*)');
  return e;
};

export const maxDob = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 15);
  return d.toISOString().split('T')[0];
};

export const calcAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth  = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-NG', { day:'numeric', month:'short', year:'numeric' }) : '—';

export const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  const datePart = date.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-NG', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return `${datePart}, ${timePart}`;
};

export const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString('en-NG', { hour:'2-digit', minute:'2-digit', hour12: false }) : '—';

const _fmtClock = (d) => d.toLocaleTimeString('en-NG', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();

// Granular relative timestamp per PRD spec:
// Just now / X mins ago / X hours ago (up to 6h) /
// Today, 6:37am / Yesterday, 6:37am / Thursday, 6:37am /
// May 7, 6:37am / May 7, 2023, 6:37am
export const timeAgo = (d) => {
  if (!d) return '';
  const now  = new Date();
  const date = new Date(d);
  const diffMs   = now - date;
  const diffSecs  = Math.floor(diffMs / 1000);
  const diffMins  = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays  = Math.floor(diffHours / 24);

  if (diffSecs < 60)   return 'Just now';
  if (diffMins < 60)   return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 6)   return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  const todayStr     = now.toDateString();
  const yesterday    = new Date(now); yesterday.setDate(now.getDate() - 1);
  const yesterdayStr = yesterday.toDateString();
  const dateStr      = date.toDateString();

  if (dateStr === todayStr)      return `Today, ${_fmtClock(date)}`;
  if (dateStr === yesterdayStr)  return `Yesterday, ${_fmtClock(date)}`;

  if (diffDays < 7) {
    const day = date.toLocaleDateString('en-US', { weekday: 'long' });
    return `${day}, ${_fmtClock(date)}`;
  }

  if (date.getFullYear() === now.getFullYear()) {
    const label = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return `${label}, ${_fmtClock(date)}`;
  }

  const label = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  return `${label}, ${_fmtClock(date)}`;
};

// fmtPostTime and smartTime use the same full granular timestamp spec
export const fmtPostTime = timeAgo;
export const smartTime  = timeAgo;

export const fmtDOB = (d) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// Online status per PRD: green = active within 5min, orange = offline < 2hrs, grey = offline >= 2hrs
export const onlineStatus = (lastSeen) => {
  if (!lastSeen) return 'offline';
  const mins = (Date.now() - new Date(lastSeen)) / 60000;
  if (mins < 5)   return 'online';
  if (mins < 120) return 'recent';
  return 'offline';
};

export const onlineColor = (status) => {
  if (status === 'online') return '#22c55e';
  if (status === 'recent') return '#f97316';
  return '#9ca3af';
};
